import store from '@/store'
import { get } from 'lodash-es'
import { getEpiSuiteDescription } from '@/components/combined-predict/models/epiSuite/epiSuiteMetadata'
import { getEpiSuiteColumnGroups } from '@/components/combined-predict/models/epiSuite/epiSuiteColumnGroups'
import { buildEpiSuiteColumnDef } from '@/components/combined-predict/models/epiSuite/epiSuiteColumnsDef'
import { getWebtestColumnGroups, getWebtestDescription } from './models/webtest/webtestMetadata'
import { buildWebtestColumnDef } from './models/webtest/webtestColumnsDef'
import { getOperaColumnGroups, getOperaDescription, getOperaName } from './models/opera/operaMetadata'
import { buildOperaColumnDef } from './models/opera/operaColumnsDef'
import { getToddsModelAdapters } from './models/toddsModels/toddsModelAdapterRegistry'
import { getToddsModelsCategory } from '@/components/combined-predict/models/toddsModels/toddsModelMetadata'
import { getPfasCategoriesAdapters } from './models/pfasCategories/pfasCategoriesAdapterRegistry'
import { getNccCategoriesAdapters } from './models/nccCategories/nccCategoriesAdapterRegistry'
import { getPfasAtlasAdapters } from './models/pfasAtlas/pfasAtlasAdapterRegistry'
import { getArnAdapters } from './models/arnCategories/arnCategoriesAdapterRegistry'
import { getAmenabilityAdapters } from './models/amenability/amenabilityAdapter'
import { formatWebtestError } from './models/webtest/webtestFormatError'
import { modelStateRegistry } from '@/utils/modelStateRegistry'
import { defaultResultsKey } from '@/utils/responseNormalizers'

const epiSuiteKeys = get(modelStateRegistry, 'epiSuite.keys')
const webtestKeys = get(modelStateRegistry, 'webtest.keys')
const operaKeys = get(modelStateRegistry, 'opera.keys')

const staticAdapters = {
  epiSuite: {
    predictionsKey: 'predictions',
    chemicalKey: 'chemical',
    errorKey: 'error',
    getResults: () => store.getters[`epiSuite/${epiSuiteKeys.getResults}`],
    getProgress: () => store.getters[`epiSuite/${epiSuiteKeys.getProgress}`],
    getIsAborted: () => store.getters[`epiSuite/${epiSuiteKeys.getIsAborted}`],
    fetch: (chemicals) => store.dispatch('epiSuite/estimateForStructures', chemicals),
    reset: () => store.commit(`epiSuite/${epiSuiteKeys.RESET_STATE}`),
    abort: () => store.commit(`epiSuite/${epiSuiteKeys.ABORT_CURRENT_FETCH}`),
    getParameters: getEpiSuiteColumnGroups,
    getColumnDefBuilder: buildEpiSuiteColumnDef,
    getName: () => 'EPI Suite™',
    getDescription: getEpiSuiteDescription,
    // `The Estimation Programs Interface (EPI) Suite™ was
    //   developed by the US Environmental Protection Agency's Office of Pollution
    //   Prevention and Toxics and SRC, Inc. It is a screening-level tool, intended for use in
    //   applications such as rapid evaluation of chemicals for release potential and
    //   "binning" chemicals by priority for future investigation.`,
  },

  webtest: {
    predictionsKey: 'endpoints',
    chemicalKey: 'chemical',
    errorKey: 'error',
    getResults: () => store.getters[`webtest/${webtestKeys.getResults}`]?.chemicals,
    getIsLoading: () => store.getters[`webtest/${webtestKeys.getIsLoading}`],
    getIsAborted: () => store.getters[`webtest/${webtestKeys.getIsAborted}`],
    formatError: (error) => formatWebtestError(error),
    fetch: (chemicals) => store.dispatch('webtest/predict', { structures: chemicals }),
    reset: () => store.commit(`webtest/${webtestKeys.RESET_STATE}`),
    abort: () => store.commit(`webtest/${webtestKeys.ABORT_CURRENT_FETCH}`), // Aborting is handled in the action by checking the signal
    getParameters: getWebtestColumnGroups,
    getColumnDefBuilder: buildWebtestColumnDef,
    getName: () => 'WebTEST v1',
    getDescription: getWebtestDescription,
  },

  opera: {
    predictionsKey: 'result',
    chemicalKey: 'chemical',
    errorKey: 'error',
    getResults: () => store.getters[`opera/${operaKeys.getResults}`]?.[defaultResultsKey],
    getProgress: () => store.getters[`opera/${operaKeys.getProgress}`],
    getProgressLabel: () => store.getters[`opera/${operaKeys.getProgressLabel}`],
    getIsAborted: () => store.getters[`opera/${operaKeys.getIsAborted}`],
    fetch: (chemicals) => store.dispatch('opera/predict', { structures: chemicals }),
    reset: () => store.commit(`opera/${operaKeys.RESET_STATE}`),
    abort: () => store.commit(`opera/${operaKeys.ABORT_CURRENT_FETCH}`),
    getParameters: getOperaColumnGroups,
    getColumnDefBuilder: buildOperaColumnDef,
    getName: getOperaName,
    getDescription: getOperaDescription,
  },
}

const getModelAdapters = () => ({
  ...staticAdapters,
  ...getToddsModelAdapters(),
  ...getPfasCategoriesAdapters(),
  ...getPfasAtlasAdapters(),
  ...getNccCategoriesAdapters(),
  ...getArnAdapters(),
  ...getAmenabilityAdapters(),
})

const getModelAdapter = (modelId) => get(getModelAdapters(), modelId)

const getModelCategories = () => [
  getToddsModelsCategory(),
  { id: 'categorizationModels', name: 'Categorization Models' },
  { id: 'amenabilityModels', name: 'Analytical Chemistry Models' },
]

export { getModelAdapter, getModelCategories, getModelAdapters }
